import { useEffect, useRef, useState, type ReactNode } from "react";
import { onIdTokenChanged, signOut as firebaseSignOut } from "firebase/auth";
import { firebaseAuth } from "@/shared/lib/firebase";
import {
  AuthContext,
  type AuthContextValue,
  type AuthRejection,
  type SessionUser,
} from "@/shared/auth/auth-context";
import { getActiveTenantId, resolveActiveTenant, setActiveTenantId } from "@/shared/tenant/active-tenant";
import { classifyVerificationError, fetchSession } from "./verify-session";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [rejection, setRejection] = useState<AuthRejection | null>(null);

  // Race guard for the async onIdTokenChanged listener: an awaited session
  // fetch from an earlier event must not clobber state applied by a later one
  // (e.g. a logout that fired while a fetch was in flight).
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current = 0;

    const unsubscribe = onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      const generation = ++generationRef.current;
      const isLatest = () => generation === generationRef.current;

      if (!firebaseUser) {
        if (!isLatest()) return;
        setUser(null);
        setStatus("unauthenticated");
        // Keep `rejection`: a not-provisioned rejection signs the user out,
        // firing this null event — LoginPage still needs the reason.
        return;
      }

      if (isLatest()) setRejection(null);

      // A valid Firebase token is necessary but NOT sufficient — the API must
      // resolve the caller to an active Growth account with a tenant. Fail
      // closed on every token event (initial sign-in AND silent refresh): if
      // the API can't confirm the session, deny rather than trust it.
      let session: SessionUser;
      try {
        session = await fetchSession();
      } catch (err) {
        if (!isLatest()) return;
        const reason = classifyVerificationError(err);
        setUser(null);
        setStatus("unauthenticated");
        setRejection(reason);
        // not-provisioned is terminal — drop the Firebase session so no stale
        // token loops on re-verification. verification-failed is transient —
        // keep it so a reload re-fires this listener and retries.
        if (reason === "not-provisioned") {
          try {
            await firebaseSignOut(firebaseAuth);
          } catch (signOutErr) {
            console.error("Failed to sign out after not-provisioned rejection", signOutErr);
          }
        }
        return;
      }

      if (!isLatest()) return;
      // Persist the tenant scope subsequent requests must carry as X-Tenant-Id.
      // The API resolves activeTenantId FROM the X-Tenant-Id we send — but on the
      // first load we've sent none, so a superadmin (who has no membership to
      // fall back to) comes back with activeTenantId=null even though `tenants`
      // lists what they can access. Precedence: the API's explicit choice, then
      // whatever we already stored, then the first accessible tenant — so
      // tenant-scoped calls work on the very first load instead of 403-ing.
      const resolvedTenant = resolveActiveTenant(
        session.activeTenantId,
        getActiveTenantId(),
        session.tenants.map((t) => t.id),
      );
      if (resolvedTenant && resolvedTenant !== getActiveTenantId()) {
        setActiveTenantId(resolvedTenant);
      }
      setUser(resolvedTenant ? { ...session, activeTenantId: resolvedTenant } : session);
      setStatus("authenticated");
      setRejection(null);
    });

    return () => {
      generationRef.current = Number.MAX_SAFE_INTEGER;
      unsubscribe();
    };
  }, []);

  const switchTenant = async (tenantId: string) => {
    // Tenant scope touches every cached query and every campaign id on screen.
    // Persist the choice and do a full document reload: React Query's cache is
    // dropped and AuthProvider re-verifies the session under the new tenant —
    // no risk of one tenant's data bleeding into another's view.
    setActiveTenantId(tenantId);
    window.location.assign("/");
  };

  const signOut = async () => {
    setRejection(null);
    setActiveTenantId(null);
    await firebaseSignOut(firebaseAuth);
  };

  return (
    <AuthContext.Provider value={{ status, user, rejection, switchTenant, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
